/**
 * AdminAiManagementTab — admin-only view of every user's chat configuration.
 *
 * What it surfaces (data from /admin/chat-management/users):
 *   - email, tier, subscription status
 *   - chat enabled (true / false / inherit-from-default)
 *   - daily / weekly USD limit + percent used + reset time
 *   - whether the user has any per-user override
 *   - last chat activity timestamp
 *
 * Actions:
 *   - Edit limits + enable/disable + note  → PUT  /users/{id}/limits
 *   - Clear override                       → DELETE /users/{id}/limits
 *   - Reset usage (zero current-window cost) → POST /users/{id}/reset-usage
 *   - Kill-switch toggle                   → POST /chat-management/kill-switch
 *
 * Designed for soft-launch ops — at the moment of writing,
 * CHAT_DEFAULT_ENABLED=false, so admins enable individual users from this tab.
 */
import { useEffect, useState } from 'preact/hooks';
import { authenticatedFetch } from '../../services/authService.js';
import { getApiBase } from './analytics/shared/formatters.js';
import { AdminAiKnowledgeSubtab } from './AdminAiKnowledgeSubtab.jsx';
import { AdminAiMySubmissionsSubtab } from './AdminAiMySubmissionsSubtab.jsx';
import { FlowsSubtab } from './flows/FlowsSubtab.jsx';
import { CertificationsEditor } from './CertificationsEditor.jsx';
import { AdminIonFeedbackSubtab } from './AdminIonFeedbackSubtab.jsx';
import { AdminIonEvalSubtab, ION_NEXT_EVAL_PRESET } from './AdminIonEvalSubtab.jsx';
import { AdminIonAnalyticsSubtab } from './AdminIonAnalyticsSubtab.jsx';
import { AdminIonKnowledgeReviewSubtab } from './AdminIonKnowledgeReviewSubtab.jsx';
import { AdminAiSystemMapSubtab } from './AdminAiSystemMapSubtab.jsx';
import { AdminIonNextSubtab } from './AdminIonNextSubtab.jsx';
import { IonTierLimitsMatrix } from './IonTierLimitsMatrix.jsx';
import { AgentConfigPanel } from './AgentConfigPanel.jsx';
import { getKnowledgeCapabilities } from '../../services/ionKnowledgeService.js';
import { getAdminRole, hasAdminPermission } from '../../state/userState.js';
import { ionHidden } from '../../state/chatStore.js';

const REFRESH_INTERVAL_MS = 30_000;

function formatRel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const min = (Date.now() - d.getTime()) / 60_000;
  if (min < 1) return 'now';
  if (min < 60) return `${Math.round(min)} min`;
  if (min < 1440) return `${Math.round(min / 60)} h`;
  return `${Math.round(min / 1440)} d`;
}

// Render a $-limit value from the admin usages row. `raw` is normally a
// decimal string ("1.2500") but is the literal string "unlimited" for
// role/internal-tester-unlimited users AND (2026-07-17 bugfix — the "$-1
// display bug") for a PRO/batemo user who self-serviced "no daily/weekly
// cap" via PUT /chat/usage/me/pacing — that write lands the same UNLIMITED
// sentinel in the per-user override column, so a plain `u.isUnlimited` check
// alone under-detects it. Checking the STRING (case-insensitive) is the
// correct guard regardless of which path produced "unlimited".
function fmtLimitUsd(raw, isUnlimited) {
  if (isUnlimited || String(raw).toLowerCase() === 'unlimited') return '∞';
  return `$${raw}`;
}

// Same guard for the read-only token-equivalent display (item 6 — token
// counts alongside $ on this admin view).
function fmtLimitTokens(raw, isUnlimited) {
  if (isUnlimited || String(raw).toLowerCase() === 'unlimited') return '∞';
  return `${raw} tok`;
}

function tierBadge(tier) {
  if (!tier) return null;
  const cls = `admin-aim-tier-pill admin-aim-tier-${tier.toLowerCase()}`;
  return <span className={cls}>{tier}</span>;
}

// The CHAT badge shows the EFFECTIVE chat-limit profile, not the admin role:
//   UNLIMITED (internal-tester flag) · PRO · DEMO (the tier whose limits apply) ·
//   DISABLED (explicitly off) · OFF (eligible but not yet switched on during
//   soft-launch) · — (free / no subscription).
//
// Since the access/quota split (2026-07-28) a privileged ROLE no longer implies
// unlimited quota — admins and knowledge experts resolve to the PRO profile and
// show the PRO badge like anyone else. The only remaining unlimited path is the
// per-user `chat_unlimited` flag, so `unlimitedReason` can only be 'internal'.
function chatStatePill(row, defaultEnabled) {
  if (row.isUnlimited) {
    const reasonLabel =
      row.unlimitedReason === 'internal' ? 'Internal tester — unlimited' : 'Unlimited';
    return (
      <span className="admin-aim-state-pill is-unlimited" title={reasonLabel}>
        UNLIMITED
      </span>
    );
  }
  if (row.chatEnabled === false) {
    return (
      <span className="admin-aim-state-pill is-off" title="Chat disabled for this account">
        DISABLED
      </span>
    );
  }
  const tier = (row.tier || '').toUpperCase();
  const hasChatTier = tier === 'PRO' || tier === 'DEMO';
  const effectivelyOn = row.chatEnabled === true || (row.chatEnabled == null && defaultEnabled);
  if (hasChatTier && effectivelyOn) {
    // Show which tier's limits the user is on — BA: "show Pro/Demo, not Default".
    return (
      <span
        className={`admin-aim-state-pill is-tier-${tier.toLowerCase()}`}
        title={`On ${tier} chat limits`}
      >
        {tier}
      </span>
    );
  }
  if (hasChatTier) {
    // Chat-eligible tier, but not switched on yet (soft-launch default is off).
    return (
      <span className="admin-aim-state-pill is-inherit" title="Not enabled yet (soft-launch default is off)">
        OFF
      </span>
    );
  }
  // FREE / no subscription → no chat at all.
  return (
    <span className="admin-aim-state-pill is-off" title="No chat (free / no subscription)">
      —
    </span>
  );
}

function progressBar(pct, isUnlimited) {
  if (isUnlimited) {
    return (
      <div className="admin-aim-bar" title="Unlimited">
        <div className="admin-aim-bar-fill admin-aim-bar-unlimited" style={{ width: '100%' }} />
        <span className="admin-aim-bar-label">∞</span>
      </div>
    );
  }
  const safe = Math.min(100, Math.max(0, pct ?? 0));
  const tone = safe >= 90 ? 'critical' : safe >= 60 ? 'warn' : 'ok';
  return (
    <div className="admin-aim-bar">
      <div className={`admin-aim-bar-fill is-${tone}`} style={{ width: `${safe}%` }} />
      <span className="admin-aim-bar-label">{safe}%</span>
    </div>
  );
}

// A per-user daily/weekly override can itself be the literal "unlimited"
// sentinel (a PRO/batemo user self-serviced "no cap" via the pacing
// endpoint — see fmtLimitUsd's docstring for the full story). This admin
// drawer has no "no cap" checkbox of its own (only the customer-facing
// self-service pacing UI does) — a blank number field already means "clear
// the override, inherit tier default" on Save, so normalize "unlimited" to
// blank here too rather than feeding an invalid string into a
// type="number" input.
function _numericOverrideOrBlank(value) {
  return value != null && String(value).toLowerCase() !== 'unlimited' ? value : '';
}

// Every subtab id, for validating `?section=` on load. Kept beside the `views`
// list below — a new subtab that is not added here simply falls back to the
// default instead of rendering nothing.
const _KNOWN_SECTIONS = new Set([
  'usages',
  'agent-config',
  'knowledge',
  'flows',
  'certifications',
  'review',
  'mysubmissions',
  'feedback',
  'eval',
  'ion-analytics',
  'systemmap',
  'ion-next-eval',
  'ion-next',
]);

function EditDrawer({ row, onClose, onSaved }) {
  const [chatEnabled, setChatEnabled] = useState(row.chatEnabled ?? null);
  const [dailyLimit, setDailyLimit] = useState(
    row.hasOverride ? _numericOverrideOrBlank(row.dailyLimitUsd) : ''
  );
  const [weeklyLimit, setWeeklyLimit] = useState(
    row.hasOverride ? _numericOverrideOrBlank(row.weeklyLimitUsd) : ''
  );
  // Ion AI limits system (2026-07): monthly override + profile override.
  const [monthlyLimit, setMonthlyLimit] = useState(row.monthlyLimitUsdOverride ?? '');
  const [tierProfileOverride, setTierProfileOverride] = useState(row.tierProfileOverride ?? '');
  const [chatUnlimited, setChatUnlimited] = useState(!!row.chatUnlimited);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [topupSaving, setTopupSaving] = useState(false);
  const [topupMessage, setTopupMessage] = useState(null);

  // A privileged ROLE (admin / knowledge expert) no longer implies unlimited
  // quota (2026-07-28 access/quota split) — those accounts resolve to the PRO
  // profile and are edited here exactly like any customer row. The per-user
  // internal-tester `chat_unlimited` flag below is the only unlimited path left.

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        chatEnabled: chatEnabled,
        dailyLimitUsd: dailyLimit === '' || dailyLimit === 'unlimited' ? null : parseFloat(dailyLimit),
        weeklyLimitUsd: weeklyLimit === '' || weeklyLimit === 'unlimited' ? null : parseFloat(weeklyLimit),
        // Monthly is admin-only for EVERY profile (DEC-B1) — set here, never
        // via the customer-facing self-service pacing endpoint.
        monthlyLimitUsd: monthlyLimit === '' || monthlyLimit === 'unlimited' ? null : parseFloat(monthlyLimit),
        tierProfileOverride: tierProfileOverride || null,
        chatUnlimited: chatUnlimited,
        note: note || null,
      };
      const res = await authenticatedFetch(
        `${getApiBase()}/admin/chat-management/users/${row.userId}/limits`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return;
      }
      onSaved();
    } catch (e) {
      setError(e?.message || 'network');
    } finally {
      setSaving(false);
    }
  };

  const handleGrantTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setTopupMessage({ type: 'error', text: 'Enter an amount > 0.' });
      return;
    }
    setTopupSaving(true);
    setTopupMessage(null);
    try {
      const res = await authenticatedFetch(
        `${getApiBase()}/admin/chat-management/users/${row.userId}/grant-topup`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amountUsd: amount, note: topupNote || null }),
        },
      );
      if (!res.ok) {
        setTopupMessage({ type: 'error', text: `HTTP ${res.status}` });
        return;
      }
      setTopupAmount('');
      setTopupNote('');
      setTopupMessage({ type: 'success', text: `+$${amount.toFixed(2)} applied to this cycle's monthly ceiling.` });
      onSaved();
    } catch (e) {
      setTopupMessage({ type: 'error', text: e?.message || 'network' });
    } finally {
      setTopupSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Override entfernen? User fällt auf Tier-Defaults zurück.')) return;
    setSaving(true);
    try {
      await authenticatedFetch(
        `${getApiBase()}/admin/chat-management/users/${row.userId}/limits`,
        { method: 'DELETE' },
      );
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Usage zurücksetzen? Aktuelle Wochen-Calls werden auf cost=0 gesetzt.')) return;
    setSaving(true);
    try {
      await authenticatedFetch(
        `${getApiBase()}/admin/chat-management/users/${row.userId}/reset-usage`,
        { method: 'POST' },
      );
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  // Demo-side reset: clears TODAY's deep-dive (cell-view) allowance (Redis
  // enforcement counter + today's DemoCellView rows the meter counts).
  const handleResetDeepDives = async () => {
    if (!window.confirm('Demo Deep-Dive Tageslimit zurücksetzen? Der heutige Zähler wird geleert, der User kann sofort wieder Zellen freischalten.')) return;
    setSaving(true);
    try {
      await authenticatedFetch(
        `${getApiBase()}/admin/demo-config/users/${row.userId}/reset-cell-views`,
        { method: 'POST' },
      );
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  // Bundled reset: Ion usage (weekly calls → cost 0) AND the demo deep-dive
  // daily allowance, in one action. Fires the two existing endpoints in order.
  const handleResetBoth = async () => {
    if (!window.confirm('Beides zurücksetzen — Ion-Usage (Wochen-Calls → cost 0) UND Demo Deep-Dive Tageslimit?')) return;
    setSaving(true);
    try {
      await authenticatedFetch(
        `${getApiBase()}/admin/demo-config/users/${row.userId}/reset-cell-views`,
        { method: 'POST' },
      );
      await authenticatedFetch(
        `${getApiBase()}/admin/chat-management/users/${row.userId}/reset-usage`,
        { method: 'POST' },
      );
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-aim-drawer-overlay" onClick={onClose}>
      <div className="admin-aim-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="admin-aim-drawer-header">
          <div className="admin-aim-drawer-headinfo">
            <div className="admin-aim-drawer-title">{row.email}</div>
            <div className="admin-aim-drawer-sub">
              {tierBadge(row.tier)} · {row.subscriptionStatus || '—'}
              {row.isUnlimited && <span className="admin-aim-state-pill is-unlimited">UNLIMITED</span>}
            </div>
          </div>
          <button type="button" className="admin-aim-icon-btn" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="admin-aim-drawer-body">
          <div className="admin-aim-field">
            <label>Unlimited (internal tester)</label>
            <div className="admin-aim-segmented">
              <button type="button"
                className={`admin-aim-seg ${chatUnlimited ? 'is-active' : ''}`}
                onClick={() => setChatUnlimited(true)}>Unlimited</button>
              <button type="button"
                className={`admin-aim-seg ${!chatUnlimited ? 'is-active' : ''}`}
                onClick={() => setChatUnlimited(false)}>Normal limits</button>
            </div>
            <p className="admin-aim-field-hint">
              Bypasses all chat quota/limits for this account regardless of tier — for
              internal testers who need to exercise Ion freely. This flag is now the
              ONLY unlimited path; an admin/knowledge-expert role grants access and
              premium models, not an unlimited budget.
            </p>
          </div>

          <div className="admin-aim-field-grid">
            <div className="admin-aim-field">
              <label>Profile</label>
              <select
                value={tierProfileOverride}
                onChange={(e) => setTierProfileOverride(e.target.value)}
              >
                <option value="">Auto ({row.tierProfile || '—'})</option>
                <option value="pro">Force PRO</option>
                <option value="demo">Force DEMO</option>
                <option value="batemo">Force batemo</option>
              </select>
              <p className="admin-aim-field-hint">
                Overrides tier + email-domain auto-detection (e.g. a batemo.de
                contractor who should NOT get the batemo rate).
              </p>
            </div>
            <div className="admin-aim-field">
              <label>Monthly $ (admin-only)</label>
              <input
                type="number" step="0.01" min="0"
                value={monthlyLimit}
                placeholder="profile default"
                onChange={(e) => setMonthlyLimit(e.target.value)}
              />
              <p className="admin-aim-field-hint">
                The plan allotment — never self-service-editable, for any profile.
                Currently: {fmtLimitUsd(row.monthlyLimitUsd, row.isUnlimited)} used ${row.monthlyUsedUsd}
                {Number(row.activeTopupUsd) > 0 ? ` (+$${row.activeTopupUsd} active top-up)` : ''}.
                {row.monthlyWindowError ? ' ⚠ monthly window unresolvable (data issue).' : ''}
              </p>
            </div>
          </div>

          {chatUnlimited ? (
            <div className="admin-aim-admin-note">
              Unlimited is ON — chat access + daily/weekly limits are bypassed for this user.
            </div>
          ) : (
            <>
              <div className="admin-aim-field">
                <label>Chat Access</label>
                <div className="admin-aim-segmented">
                  <button type="button"
                    className={`admin-aim-seg ${chatEnabled === true ? 'is-active' : ''}`}
                    onClick={() => setChatEnabled(true)}>Enabled</button>
                  <button type="button"
                    className={`admin-aim-seg ${chatEnabled === false ? 'is-active' : ''}`}
                    onClick={() => setChatEnabled(false)}>Disabled</button>
                  <button type="button"
                    className={`admin-aim-seg ${chatEnabled == null ? 'is-active' : ''}`}
                    onClick={() => setChatEnabled(null)}>Inherit default</button>
                </div>
              </div>

              <div className="admin-aim-field-grid">
                <div className="admin-aim-field">
                  <label>Daily $</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={dailyLimit}
                    placeholder="tier default"
                    onChange={(e) => setDailyLimit(e.target.value)}
                  />
                </div>
                <div className="admin-aim-field">
                  <label>Weekly $</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={weeklyLimit}
                    placeholder="tier default"
                    onChange={(e) => setWeeklyLimit(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <div className="admin-aim-field">
            <label>Note (audit-log)</label>
            <input
              type="text"
              value={note}
              maxLength={255}
              placeholder="z.B. trial-bump nach support-ticket"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {error && <div className="admin-aim-error">{error}</div>}

          {/* Top-ups apply to every row now that a privileged role no longer
              waives the monthly ceiling — an admin burning through PRO's
              monthly allotment mid-eval is exactly the case this covers. */}
          <div className="admin-aim-topup-section">
            <label>Grant top-up (this cycle only)</label>
            <p className="admin-aim-field-hint">
              A one-off increase to this user's MONTHLY ceiling for the CURRENT
              cycle only — not a base-limit change (use "Monthly $" above for that).
            </p>
            <div className="admin-aim-field-grid">
              <div className="admin-aim-field">
                <label>Amount ($)</label>
                <input
                  type="number" step="0.01" min="0"
                  value={topupAmount}
                  placeholder="e.g. 25.00"
                  onChange={(e) => setTopupAmount(e.target.value)}
                />
              </div>
              <div className="admin-aim-field">
                <label>Note</label>
                <input
                  type="text"
                  value={topupNote}
                  maxLength={255}
                  placeholder="reason"
                  onChange={(e) => setTopupNote(e.target.value)}
                />
              </div>
            </div>
            {topupMessage && (
              <div className={topupMessage.type === 'error' ? 'admin-aim-error' : 'admin-aim-success'}>
                {topupMessage.text}
              </div>
            )}
            <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
              onClick={handleGrantTopup} disabled={topupSaving}>
              {topupSaving ? 'Granting…' : 'Grant top-up'}
            </button>
          </div>
        </div>

        <footer className="admin-aim-drawer-footer">
          {/* Only genuinely unlimited accounts (the `chat_unlimited` flag) have no
              window to zero. Admin/expert rows are resettable again — their role
              stopped implying unlimited quota. */}
          <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
            onClick={handleReset} disabled={saving || row.isUnlimited}
            title={row.isUnlimited ? 'Unlimited (internal tester) accounts have no quota to reset.' : 'Zero this user’s current Ion usage window.'}>
            Reset usage
          </button>
          <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
            onClick={handleResetDeepDives} disabled={saving}
            title="Reset today’s demo deep-dive (cell-view) allowance.">
            Reset deep dives
          </button>
          <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
            onClick={handleResetBoth} disabled={saving}
            title="Reset both Ion usage and the demo deep-dive daily allowance.">
            Reset both
          </button>
          <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
            onClick={handleClear} disabled={saving || !row.hasOverride}>
            Clear override
          </button>
          <button type="button" className="admin-ai-btn admin-ai-btn--send"
            onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function KillSwitchBar({ active, onToggle }) {
  const handleToggle = async () => {
    const next = !active;
    const verb = next ? 'AKTIVIEREN' : 'deaktivieren';
    if (!window.confirm(`Kill-switch ${verb}? ${next ? 'Alle non-admin Chat-Requests werden 403.' : 'Chat wird wieder freigegeben.'}`)) return;
    await authenticatedFetch(`${getApiBase()}/admin/chat-management/kill-switch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next }),
    });
    onToggle?.();
  };
  return (
    <div className={`admin-aim-killswitch ${active ? 'is-active' : ''}`}>
      <span className="admin-aim-killswitch-label">Kill-switch</span>
      <span className={`admin-aim-killswitch-state ${active ? 'is-on' : 'is-off'}`}>
        <span className="admin-aim-killswitch-dot" />
        {active ? 'ACTIVE — chat blocked for non-admin' : 'inactive'}
      </span>
      <button
        type="button"
        className={`admin-aim-killswitch-btn ${active ? '' : 'is-danger'}`}
        onClick={handleToggle}
        title={active ? 'Re-enable chat' : 'Block all non-admin chat traffic'}
      >
        {active ? 'Deactivate' : 'Activate'}
      </button>
    </div>
  );
}

/**
 * AdminAiManagementTab — top-level container with two sub-tabs:
 *   - Usages    (existing per-user chat config / limits / kill-switch)
 *   - Knowledge (admin-editable AI knowledge entries)
 *
 * Layout mirrors AdminAnalyticsTab: top horizontal `analytics-nav`
 * pills + content area. Reuses the analytics-* classes so style stays
 * consistent across the admin sidebar.
 */
export function AdminAiManagementTab() {
  // Knowledge experts are locked to authoring: Knowledge + My Submissions only
  // (+ Review if they are also an approver). Full admins see everything. The
  // expert check is synchronous (role is on currentUser), so it can pick the
  // initial subtab; approver status loads async (capabilities endpoint).
  const isExpert = getAdminRole() === 'knowledge_expert';
  // Ion Next (admin-only native engine): never for knowledge experts, and only
  // with the permission its routes enforce (backend admin/ion_next.py, AI_MANAGE).
  const canIonNext = !isExpert && hasAdminPermission('ai.manage');
  // The subtab lives in the URL (`?section=flows`). Without it a reload always
  // dropped you back on Usages, which is a long way from AI Flows when you are
  // iterating on one flow — and a subtab you cannot link to is one you cannot
  // send anyone. A query param rather than a path segment: the admin route is
  // `/admin/:tab` and adding a second segment would mean touching the router.
  const [activeView, setActiveViewState] = useState(() => {
    const wanted = new URLSearchParams(window.location.search).get('section');
    // Trust it only if it is a real subtab; a stale or hand-typed value falls
    // back rather than rendering an empty content area.
    if (wanted && _KNOWN_SECTIONS.has(wanted)) return wanted;
    return isExpert ? 'knowledge' : 'usages';
  });

  const setActiveView = (id) => {
    setActiveViewState(id);
    const url = new URL(window.location.href);
    url.searchParams.set('section', id);
    // replaceState, not pushState: switching subtabs is not navigation, and
    // filling the back button with them would bury the page you came from.
    window.history.replaceState({}, '', url);
  };
  // The Review tab + its pending badge only show for knowledge approvers.
  const [isApprover, setIsApprover] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getKnowledgeCapabilities()
      .then((c) => {
        if (cancelled) return;
        setIsApprover(!!c.is_approver);
        setPendingCount(c.pending_count || 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeView]);

  // Authoring vs ops grouping. Knowledge experts author Knowledge + AI Flows
  // (both queue for approval) and track their own My Submissions; everything
  // else is full-admin. Approvers additionally get the Review queue.
  const views = [
    ...(isExpert ? [] : [{ id: 'usages', label: 'Usages' }]),
    // Dedicated subtab (LOCKED §0.2 — NOT folded into Usages): env-wide
    // temperature/model/agent-loop/cache/web-search/rate-limit config. The
    // per-user table + EditDrawer in Usages are untouched by this feature.
    ...(isExpert ? [] : [{ id: 'agent-config', label: 'Agent Config' }]),
    { id: 'knowledge', label: 'Knowledge' },
    { id: 'flows', label: 'AI Flows' },
    ...(isExpert ? [] : [{ id: 'certifications', label: 'Certifications' }]),
    ...(isApprover ? [{ id: 'review', label: 'Review', badge: pendingCount }] : []),
    { id: 'mysubmissions', label: 'My Submissions' },
    ...(isExpert ? [] : [{ id: 'feedback', label: 'Feedback' }]),
    ...(isExpert ? [] : [{ id: 'eval', label: 'Eval' }]),
    ...(isExpert ? [] : [{ id: 'ion-analytics', label: 'Ion Analytics' }]),
    ...(isExpert ? [] : [{ id: 'systemmap', label: 'System Map' }]),
    ...(canIonNext ? [{ id: 'ion-next', label: 'Ion Next' }] : []),
    // The eval dashboard preset to Ion Next (native:ion, Opus 5.5, showcase suite).
    ...(canIonNext ? [{ id: 'ion-next-eval', label: 'Ion Next eval' }] : []),
  ];

  return (
    <div className="admin-analytics-tab">
      <div className="analytics-header">
        <div className="analytics-nav" role="tablist" aria-label="AI Management sections">
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={activeView === v.id}
              className={`analytics-nav-btn ${activeView === v.id ? 'active' : ''}`}
              onClick={() => setActiveView(v.id)}
            >
              <span>{v.label}</span>
              {v.badge > 0 && <span className="admin-aim-nav-badge">{v.badge}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="analytics-content">
        {activeView === 'usages' && !isExpert && <AdminAiUsagesSubtab />}
        {activeView === 'agent-config' && !isExpert && <AgentConfigPanel />}
        {activeView === 'knowledge' && (
          <AdminAiKnowledgeSubtab onGoToMySubmissions={() => setActiveView('mysubmissions')} />
        )}
        {activeView === 'mysubmissions' && <AdminAiMySubmissionsSubtab />}
        {activeView === 'flows' && <FlowsSubtab />}
        {activeView === 'certifications' && !isExpert && <CertificationsEditor />}
        {activeView === 'review' && isApprover && <AdminIonKnowledgeReviewSubtab />}
        {activeView === 'feedback' && !isExpert && <AdminIonFeedbackSubtab />}
        {activeView === 'eval' && !isExpert && <AdminIonEvalSubtab />}
        {activeView === 'ion-analytics' && !isExpert && <AdminIonAnalyticsSubtab />}
        {activeView === 'systemmap' && !isExpert && <AdminAiSystemMapSubtab />}
        {activeView === 'ion-next' && canIonNext && <AdminIonNextSubtab />}
        {activeView === 'ion-next-eval' && canIonNext && <AdminIonEvalSubtab preset={ION_NEXT_EVAL_PRESET} />}
      </div>
    </div>
  );
}


function AdminAiUsagesSubtab() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [killSwitchActive, setKillSwitchActive] = useState(false);
  const [chatDefaultEnabled, setChatDefaultEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  // Token-count display toggle (Ion limits UX pass, 2026-07-17) — $ is
  // primary/default; tokens ($0.25/token) are an admin-only convenience so
  // magnitudes stay legible (item 6). Mirrors the same toggle pattern on
  // AnalyticsAiUsageView.
  const [showTokens, setShowTokens] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (search.trim()) params.set('search', search.trim());
      if (onlyActive) params.set('only_active_chat', 'true');
      const res = await authenticatedFetch(
        `${getApiBase()}/admin/chat-management/users?${params}`,
      );
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
      setKillSwitchActive(!!data.killSwitchActive);
      setChatDefaultEnabled(!!data.chatDefaultEnabled);
    } catch (e) {
      setError(e?.message || 'network');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    const t = setInterval(fetchUsers, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, onlyActive]);

  const handleSaved = () => {
    setEditingRow(null);
    fetchUsers();
  };

  return (
    <div className="admin-aim-subtab-body">
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">Usages</h2>
          <p className="admin-aim-sub">
            Per-user chat enablement + limits. Soft-launch: admin enables
            individual users while CHAT_DEFAULT_ENABLED is false.
          </p>
        </div>
        <KillSwitchBar active={killSwitchActive} onToggle={fetchUsers} />
      </header>

      {/* Tier-wide defaults every non-overridden user of that tier inherits
          (per-user overrides are edited in the table below). PRO and DEMO are
          independent and each tunable here. The Demo tab shows the same DEMO
          panel (shared component — they can't drift). Admin-only; the whole tab
          already enforces RBAC. */}
      {!ionHidden.value && (
        <div className="admin-aim-tier-section">
          {/* One shared hint for all four panels (each panel used to repeat it,
              which cluttered the row and pushed each column's first card to a
              different height). */}
          <p className="admin-demo-hint admin-aim-tier-hint">
            Tune Ion’s per-tier limits live — no deploy needed. Changes apply to
            new requests within about a minute. A field showing{' '}
            <span className="admin-demo-badge admin-demo-badge--override">overridden</span>{' '}
            uses this value; <span className="admin-demo-badge">default</span>{' '}
            falls back to the environment default.
          </p>
          {/* Budgets × tiers matrix (PRO/DEMO/batemo/UNLIMITED). Replaced the
              four side-by-side panels whose varying-length headings never
              lined up. UNLIMITED is the runaway-brake tier (daily only). The
              single-tier IonTierLimitsPanel is still used by the Demo tab. */}
          <IonTierLimitsMatrix />
        </div>
      )}

      <div className="admin-aim-toolbar">
        <input
          type="search"
          className="admin-aim-search"
          placeholder="Search by email…"
          value={search}
          onInput={(e) => setSearch(e.target.value)}
        />
        <label className="admin-aim-checkbox">
          <input type="checkbox" checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)} />
          Only active this week
        </label>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost"
          onClick={() => setShowTokens((v) => !v)}
          title="Toggle the Daily/Weekly/Monthly columns between USD and Insights Tokens ($0.25/token)"
        >
          {showTokens ? 'Show USD' : 'Show Insights Tokens'}
        </button>
        <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={fetchUsers}>↻</button>
        <span className="admin-aim-meta">{total} users total</span>
      </div>

      {error && <div className="admin-aim-error-banner">{error}</div>}

      <div className="admin-aim-table-wrap">
        <table className="admin-aim-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Tier</th>
              <th>Profile</th>
              <th>Chat</th>
              <th>Daily</th>
              <th>Weekly</th>
              <th>Monthly</th>
              <th>Last activity</th>
              <th>Override</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {/* Row highlight: the `is-admin` CSS class name is legacy — it marks
                UNLIMITED rows, which since the access/quota split means the
                per-user internal-tester flag only, no longer admin/expert roles.
                Kept as-is to avoid a stylesheet rename outside this change. */}
            {loading && users.length === 0 ? (
              <tr><td colSpan={10} className="admin-aim-empty">Lade…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={10} className="admin-aim-empty">Keine User.</td></tr>
            ) : users.map((u) => (
              <tr key={u.userId} className={u.isUnlimited ? 'is-admin' : ''}>
                <td>
                  <div className="admin-aim-user-cell">
                    <span className="admin-aim-user-email">{u.email}</span>
                    {u.firstName || u.lastName ? (
                      <span className="admin-aim-user-name">
                        {[u.firstName, u.lastName].filter(Boolean).join(' ')}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td>{tierBadge(u.tier)}</td>
                <td>
                  <span className={`admin-aim-profile-pill is-${u.tierProfile || 'none'}`}>
                    {u.tierProfile === 'batemo' ? 'batemo' : (u.tierProfile || '—')}
                  </span>
                  {u.monthlyWindowError && (
                    <span title="Monthly window unresolvable (data issue)"> ⚠</span>
                  )}
                </td>
                <td>{chatStatePill(u, chatDefaultEnabled)}</td>
                <td>
                  {progressBar(u.dailyUsedPct, u.isUnlimited)}
                  <div className="admin-aim-bar-meta">
                    {showTokens
                      ? `${u.dailyUsedTokens} / ${fmtLimitTokens(u.dailyLimitTokens, u.isUnlimited)}`
                      : `$${u.dailyUsedUsd} / ${fmtLimitUsd(u.dailyLimitUsd, u.isUnlimited)}`}
                  </div>
                </td>
                <td>
                  {progressBar(u.weeklyUsedPct, u.isUnlimited)}
                  <div className="admin-aim-bar-meta">
                    {showTokens
                      ? `${u.weeklyUsedTokens} / ${fmtLimitTokens(u.weeklyLimitTokens, u.isUnlimited)}`
                      : `$${u.weeklyUsedUsd} / ${fmtLimitUsd(u.weeklyLimitUsd, u.isUnlimited)}`}
                  </div>
                </td>
                <td>
                  {progressBar(u.monthlyUsedPct, u.isUnlimited)}
                  <div className="admin-aim-bar-meta">
                    {showTokens
                      ? `${u.monthlyUsedTokens} / ${fmtLimitTokens(u.monthlyLimitTokens, u.isUnlimited)}`
                      : `$${u.monthlyUsedUsd} / ${fmtLimitUsd(u.monthlyLimitUsd, u.isUnlimited)}`}
                    {Number(u.activeTopupUsd) > 0 ? ` (+$${u.activeTopupUsd})` : ''}
                  </div>
                </td>
                <td className="admin-aim-rel">{formatRel(u.lastActivityAt)}</td>
                <td>{u.hasOverride ? '✓' : '—'}</td>
                <td>
                  <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
                    onClick={() => setEditingRow(u)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingRow && (
        <EditDrawer row={editingRow} onClose={() => setEditingRow(null)} onSaved={handleSaved} />
      )}
    </div>
  );
}
